import { type InvoicePreviewResponse } from '@utazzo/shared';
import { UzCard, UzNumberInput, UzSpinner } from '@utazzo/ui';
import React from 'react';

import { formatInrPaise } from '../invoices.helpers';

import { type GstSlab } from './invoice-form.helpers';

interface PricingSectionProps {
  gstSlab: GstSlab | '';
  isFlightOnly: boolean;
  // Flight-only breakdown
  basic: number | '';
  onBasic: (v: number | '') => void;
  yq: number | '';
  onYq: (v: number | '') => void;
  seatCharge: number | '';
  onSeatCharge: (v: number | '') => void;
  mealCharge: number | '';
  onMealCharge: (v: number | '') => void;
  taxes: number | '';
  onTaxes: (v: number | '') => void;
  discountPercent: number | '';
  onDiscountPercent: (v: number | '') => void;
  // Non-flight
  saleAmount: number | '';
  onSaleAmount: (v: number | '') => void;
  // Derived + live GST
  saleAmountPaise: number;
  preview: InvoicePreviewResponse | null;
  previewLoading: boolean;
  showErrors: boolean;
  saleMissing: boolean;
}

const Money = ({
  label,
  paise,
}: {
  label: string;
  paise: string | undefined;
}): React.JSX.Element => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-zinc-500">{label}</span>
    <span className="font-medium text-zinc-800 tabular-nums">
      {paise === undefined ? '-' : formatInrPaise(paise)}
    </span>
  </div>
);

/**
 * Section 9 - Pricing. Flight-only invoices show a Basic/YQ/Seat/Meal/Taxes/
 * Discount breakdown (the sale base is their sum, less the discount %);
 * everything else is a single Sale Amount. The live GST panel reflects the
 * debounced `POST /invoices/preview` result (taxable base, service charge,
 * CGST/SGST/IGST and grand total).
 */
export const PricingSection = ({
  gstSlab,
  isFlightOnly,
  basic,
  onBasic,
  yq,
  onYq,
  seatCharge,
  onSeatCharge,
  mealCharge,
  onMealCharge,
  taxes,
  onTaxes,
  discountPercent,
  onDiscountPercent,
  saleAmount,
  onSaleAmount,
  saleAmountPaise,
  preview,
  previewLoading,
  showErrors,
  saleMissing,
}: PricingSectionProps): React.JSX.Element => {
  const serviceChargeEditable = gstSlab === 'CRP' || gstSlab === 'PKG';

  return (
    <UzCard title="Pricing" description="Sale amount and the GST carved out of it.">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          {isFlightOnly ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <UzNumberInput
                  label="Basic (Rs)"
                  min={0}
                  value={basic}
                  onValueChange={(v) => onBasic(v ?? '')}
                />
                <UzNumberInput
                  label="YQ (Rs)"
                  min={0}
                  value={yq}
                  onValueChange={(v) => onYq(v ?? '')}
                />
                <UzNumberInput
                  label="Seat charge (Rs)"
                  min={0}
                  value={seatCharge}
                  onValueChange={(v) => onSeatCharge(v ?? '')}
                />
                <UzNumberInput
                  label="Meal charge (Rs)"
                  min={0}
                  value={mealCharge}
                  onValueChange={(v) => onMealCharge(v ?? '')}
                />
                <UzNumberInput
                  label="Taxes (Rs)"
                  min={0}
                  value={taxes}
                  onValueChange={(v) => onTaxes(v ?? '')}
                />
                <UzNumberInput
                  label="Discount (%)"
                  precision={2}
                  min={0}
                  max={100}
                  value={discountPercent}
                  onValueChange={(v) => onDiscountPercent(v ?? '')}
                />
              </div>
              <div className="flex items-center justify-between border-t border-zinc-100 pt-3 text-sm">
                <span className="text-zinc-500">Computed sale amount</span>
                <span
                  className={`font-medium tabular-nums ${showErrors && saleMissing ? 'text-red-600' : 'text-zinc-900'}`}
                >
                  {formatInrPaise(String(saleAmountPaise))}
                </span>
              </div>
            </>
          ) : (
            <UzNumberInput
              label="Sale amount (Rs)"
              hint="Gross customer-payable amount; GST is carved out of it."
              required
              min={0}
              value={saleAmount}
              onValueChange={(v) => onSaleAmount(v ?? '')}
              error={showErrors && saleMissing ? 'Enter a sale amount' : undefined}
            />
          )}
        </div>

        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
              Live GST
            </span>
            {previewLoading && (
              <span className="flex items-center gap-1 text-xs text-zinc-400">
                <UzSpinner size="sm" /> computing...
              </span>
            )}
          </div>
          <div className="space-y-2">
            <Money label="Taxable base" paise={preview?.taxableBasePaise} />
            <Money
              label={`Service charge${serviceChargeEditable ? '' : ' (auto)'}`}
              paise={preview?.serviceChargePaise}
            />
            <Money label="CGST" paise={preview?.cgstPaise} />
            <Money label="SGST" paise={preview?.sgstPaise} />
            <Money label="IGST" paise={preview?.igstPaise} />
            {preview && preview.tcsPaise !== '0' && (
              <>
                <Money label="Gross total" paise={preview.grossTotalPaise} />
                <Money label="TCS" paise={preview.tcsPaise} />
              </>
            )}
            <div className="border-t border-zinc-200 pt-2">
              <div className="flex items-center justify-between text-sm font-semibold">
                <span className="text-zinc-700">Grand total</span>
                <span className="text-zinc-900 tabular-nums">
                  {preview ? formatInrPaise(preview.grandTotalPaise) : '-'}
                </span>
              </div>
            </div>
            {preview && (
              <p className="pt-1 text-xs text-zinc-400">Place of supply: {preview.placeOfSupply}</p>
            )}
          </div>
        </div>
      </div>
    </UzCard>
  );
};
